// src/app/authProvider.ts
import type { AuthProvider } from "react-admin";
import { apiUrl } from "./httpClient";
import { handleApiError, isAuthError } from "./utils/errorHandler";
import { ADMIN_BASE } from "./config";

type AdminRole = { role: string };
type AdminUser = {
  id: string;
  username: string;
  name: string;
  roles?: AdminRole[];
};

type LoginResponse = {
  success: boolean;
  user: AdminUser;
  accessToken: string;
  refreshToken: string;
};

export const authProvider: AuthProvider = {
  // react-admin will call this when you submit the Login form
  login: async ({ username, password }) => {
    const res = await fetch(apiUrl(`${ADMIN_BASE}/auth/login`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      // 401 -> invalid creds; 403 -> not admin, both are "reject"
      const msg =
        res.status === 401
          ? "Invalid email/password. Please try again."
          : res.status === 403
            ? "Access denied. Admin privileges required."
            : "Login failed";
      throw new Error(msg);
    }

    const data = (await res.json()) as LoginResponse;
    // Persist tokens & user
    localStorage.setItem("tn_admin_access", data.accessToken);
    localStorage.setItem("tn_admin_refresh", data.refreshToken);
    localStorage.setItem("tn_admin_user", JSON.stringify(data.user));
  },

  // called by <LogoutButton/> or when session expires and you decide to force logout
  logout: async () => {
    const access = localStorage.getItem("tn_admin_access");
    try {
      await fetch(apiUrl(`${ADMIN_BASE}/auth/logout`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
        },
      });
    } catch {
      // ignore network errors on logout
    } finally {
      localStorage.removeItem("tn_admin_access");
      localStorage.removeItem("tn_admin_refresh");
      localStorage.removeItem("tn_admin_user");
    }
  },

  // called on every navigation; reject to redirect to /login
  checkAuth: () =>
    localStorage.getItem("tn_admin_access")
      ? Promise.resolve()
      : Promise.reject(),

  // called on dataProvider errors
  checkError: (error: unknown) => {
    const appError = handleApiError(error);
    if (isAuthError(appError)) {
      localStorage.removeItem("tn_admin_access");
      // keep refresh token if you plan to implement refresh later
      return Promise.reject();
    }
    return Promise.resolve();
  },

  // Optional: expose role(s) to auth-aware UI
  getPermissions: async () => {
    const raw = localStorage.getItem("tn_admin_user");
    const user: AdminUser | null = raw ? JSON.parse(raw) : null;
    // react-admin doesn't require a specific shape; return what you need
    const roles = user?.roles?.map((r) => r.role) ?? [];
    return roles; // e.g., ['admin']
  },

  // Optional: display name/avatar in the top bar
  getIdentity: async () => {
    const raw = localStorage.getItem("tn_admin_user");
    const user: AdminUser | null = raw ? JSON.parse(raw) : null;
    return {
      id: user?.id ?? "me",
      fullName: user?.name ?? user?.username ?? "Admin",
      // avatar: '/path/to/avatar.png',
    };
  },
};
